import { useState } from "react";
import { useConflicts, useModelConfigs } from "../hooks/useIngestion";
import ConflictDiffViewer from "../components/ConflictDiffViewer";
import ConflictActions from "../components/ConflictActions";
import { ConflictRecord } from "../lib/ingestion";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

/** Radix `Select.Item` forbids an empty-string value (reserved for
 *  "nothing selected") — same sentinel pattern as `ReferenceValueSelect`. */
const UNSET = "__unset__";

export default function ConflictsView() {
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [selectedModel, setSelectedModel] = useState<string>("");
  const [selectedConflict, setSelectedConflict] =
    useState<ConflictRecord | null>(null);

  const { data: configs } = useModelConfigs();
  const {
    data: conflictsData,
    isLoading,
    error,
    refetch,
  } = useConflicts({
    target_model: selectedModel || undefined,
    status: "open",
    page,
    page_size: pageSize,
  });

  const conflicts = conflictsData?.conflicts || [];
  const total = conflictsData?.total || 0;
  const totalPages = Math.ceil(total / pageSize);

  const modelOptions = configs
    ? Object.values(configs).map((c) => c.model_key)
    : [];

  const handleConflictSelect = (conflict: ConflictRecord) => {
    setSelectedConflict(conflict);
  };

  const handleActionSuccess = () => {
    // Refresh the list and close the detail view
    refetch();
    setSelectedConflict(null);
  };

  return (
    <div className="container mx-auto px-4 py-8" data-testid="conflicts-view">
      <div className="mb-6">
        <h1
          className="text-3xl font-bold text-gray-900"
          data-testid="conflicts-heading"
        >
          Conflict Resolution
        </h1>
        <p className="text-gray-600 mt-2">
          Review and resolve ingestion conflicts where incoming data matches
          existing records
        </p>
      </div>

      {/* Filters */}
      <div
        className="rounded-xl border border-gray-200 bg-white shadow-sm p-4 mb-6"
        data-testid="conflicts-filters"
      >
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <label
              htmlFor="conflicts-model-filter"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Filter by Model
            </label>
            <Select
              value={selectedModel === "" ? UNSET : selectedModel}
              onValueChange={(next) => {
                setSelectedModel(next === UNSET ? "" : next);
                setPage(1);
              }}
            >
              <SelectTrigger
                id="conflicts-model-filter"
                className="w-full"
                data-testid="conflicts-model-filter"
              >
                <SelectValue>
                  {selectedModel === "" ? "All Models" : selectedModel}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={UNSET}>All Models</SelectItem>
                {modelOptions.map((modelKey) => (
                  <SelectItem key={modelKey} value={modelKey}>
                    {modelKey}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-end">
            <button
              data-testid="conflicts-refresh-btn"
              onClick={() => refetch()}
              className="inline-flex items-center justify-center gap-2 font-medium rounded-lg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed px-4 py-2 text-sm bg-primary-600 text-white hover:bg-primary-700 active:bg-primary-800"
            >
              Refresh
            </button>
          </div>
        </div>

        <div
          className="mt-4 text-sm text-gray-500"
          data-testid="conflicts-count"
        >
          Showing {conflicts.length} of {total} open conflicts
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Conflicts List */}
        <div
          className="rounded-xl border border-gray-200 bg-white shadow-sm"
          data-testid="conflicts-list-panel"
        >
          <div className="border-b border-gray-200 px-6 py-4">
            <h2 className="text-lg font-semibold text-gray-900">Conflicts</h2>
          </div>

          {isLoading && (
            <div
              className="p-8 text-center text-sm text-gray-500"
              role="status"
              aria-live="polite"
              data-testid="conflicts-loading"
            >
              Loading conflicts...
            </div>
          )}

          {error && (
            <div
              className="p-8 text-center text-sm text-red-700"
              data-testid="conflicts-error"
            >
              Error loading conflicts:{" "}
              {error instanceof Error ? error.message : "Unknown error"}
            </div>
          )}

          {!isLoading && !error && conflicts.length === 0 && (
            <div
              className="p-8 text-center text-sm text-gray-500"
              data-testid="conflicts-empty"
            >
              No open conflicts found. Great job!
            </div>
          )}

          {!isLoading && !error && conflicts.length > 0 && (
            <div
              className="divide-y divide-gray-100"
              data-testid="conflicts-list"
            >
              {conflicts.map((conflict) => (
                <div
                  key={conflict.conflict_id}
                  data-testid={`conflict-row-${conflict.conflict_id}`}
                  onClick={() => handleConflictSelect(conflict)}
                  className={`px-4 py-4 cursor-pointer hover:bg-gray-50 transition-colors ${
                    selectedConflict?.conflict_id === conflict.conflict_id
                      ? "bg-primary-50 border-l-4 border-primary-600"
                      : ""
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-900">
                      {conflict.target_model}
                    </span>
                    <span className="text-xs text-gray-500">
                      {(conflict.similarity_score * 100).toFixed(1)}% match
                    </span>
                  </div>

                  <div className="text-xs text-gray-500 font-mono mb-2">
                    ID: {conflict.conflict_id.slice(0, 16)}...
                  </div>

                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-500">
                      {Object.keys(conflict.fields_diff).length} fields differ
                    </span>
                    <span className="text-gray-500">
                      {new Date(conflict.created_at).toLocaleString()}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="border-t border-gray-200 px-4 py-3 flex items-center justify-between">
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page === 1}
                className="inline-flex items-center justify-center gap-2 font-medium rounded-lg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed px-3 py-1.5 text-xs bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 active:bg-gray-100"
              >
                Previous
              </button>
              <span className="text-sm text-gray-500">
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                disabled={page === totalPages}
                className="inline-flex items-center justify-center gap-2 font-medium rounded-lg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed px-3 py-1.5 text-xs bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 active:bg-gray-100"
              >
                Next
              </button>
            </div>
          )}
        </div>

        {/* Conflict Detail */}
        <div className="space-y-4" data-testid="conflict-detail-panel">
          {selectedConflict ? (
            <>
              <ConflictDiffViewer conflict={selectedConflict} />
              <ConflictActions
                conflict={selectedConflict}
                ingestionId={selectedConflict.ingestion_item_id.split("-")[0]} // Extract batch ID from item ID
                modelConfig={
                  configs ? configs[selectedConflict.target_model] : undefined
                }
                onSuccess={handleActionSuccess}
              />
            </>
          ) : (
            <div
              className="rounded-xl border border-gray-200 bg-white shadow-sm p-8 text-center text-sm text-gray-500"
              data-testid="conflict-detail-empty"
            >
              Select a conflict from the list to view details and take action
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
